import { type ReactNode, use, useEffect, useMemo, useState } from 'react'
import { ChangeBaseParametersOverlay } from '#pages/Configuration/ChangeBaseParameters/ChangeBaseParametersOverlay'
import type { TruckConfiguration } from '#store/slices/configurationSlice'
import { InternalChangeBaseParametersOverlayContext } from './InternalChangeBaseParametersOverlayContext'

type ChangeBaseParametersOverlayState =
  | {
      isShown: false
      configuration?: undefined
    }
  | {
      isShown: true
      configuration: TruckConfiguration
      displayMode?: 'part-period' | 'full'
    }

export type ChangeBaseParametersContextData = {
  getState: () => ChangeBaseParametersOverlayState
  showOverlay: (state: ChangeBaseParametersOverlayState) => void
  subscribe: (handler: (state: ChangeBaseParametersOverlayState) => void) => () => void
}

const ContextAwareChangeBaseParametersInformationOverlay = () => {
  const context = use(InternalChangeBaseParametersOverlayContext)
  if (!context) {
    throw new Error('ManCodeOverlayContext not found')
  }

  const [state, setState] = useState<ChangeBaseParametersOverlayState>(context.getState())

  useEffect(() => {
    return context.subscribe((state) => {
      setState(state)
    })
  }, [context.subscribe])

  return state.isShown ? (
    <ChangeBaseParametersOverlay
      isOpen={true}
      setIsOpen={() => context?.showOverlay({ isShown: false })}
      configuration={state.configuration}
      displayMode={state.displayMode}
    />
  ) : null
}

export const ChangeBaseParametersOverlayProvider = ({ children }: { children: ReactNode }) => {
  const contextData = useMemo(() => {
    let state: ChangeBaseParametersOverlayState = {
      isShown: false,
    }
    const eventHandlerSet = new Set<(state: ChangeBaseParametersOverlayState) => void>()
    const notify = () => {
      for (const handler of eventHandlerSet) {
        handler(state)
      }
    }

    return {
      getState: () => state,
      showOverlay: (newState: ChangeBaseParametersOverlayState) => {
        state = newState
        notify()
      },
      subscribe: (handler: (state: ChangeBaseParametersOverlayState) => void) => {
        eventHandlerSet.add(handler)
        return () => {
          eventHandlerSet.delete(handler)
        }
      },
    } satisfies ChangeBaseParametersContextData
  }, [])

  return (
    <InternalChangeBaseParametersOverlayContext.Provider value={contextData}>
      {children}
      <ContextAwareChangeBaseParametersInformationOverlay />
    </InternalChangeBaseParametersOverlayContext.Provider>
  )
}
